import { createHash } from "node:crypto";
import { isMap, isSeq, parseDocument } from "yaml";
import { z } from "zod";
import type { DataverseApi } from "../api/dataverse-api.js";
import { ApiError, ValidationError } from "../utils/errors.js";
import { GUID_PATTERN, escapeOData } from "../utils/security.js";

// Experimental authoring, not a tenant/native-serializer roundtrip claim.
// Microsoft YAML schema: MultiTurnEvaluationCase -> MultiTurnActivityNoKind -> Message.
// https://raw.githubusercontent.com/microsoft/skills-for-copilot-studio/1838950fa95f1d2f8365e826acd8a600ff593bd9/reference/bot.schema.yaml-authoring.json
// Native 41135 builds alternating question/reference-answer Message activities:
// https://content.powerapps.com/resource/makerx/static/js/module/EvaluationHubPage~age~07fb0791.603fd9d7.chunk.js
// Native 41136 defines user/agent (not the live transcript's bot role):
// https://content.powerapps.com/resource/makerx/static/js/module/ChatPane~EvaluationHu~57ead0e5.e719d887.chunk.js
// https://learn.microsoft.com/en-us/power-apps/developer/data-platform/reference/entities/botcomponent
const guid = z.string().length(36).regex(GUID_PATTERN);
const literalText = z
  .string()
  .min(1)
  .max(1000)
  .refine(
    (value) =>
      value.trim().length > 0 &&
      !/[{}\p{Cf}]/u.test(value) &&
      !/[\p{Cc}]/u.test(value.replaceAll("\n", "")) &&
      !/^\s*=/u.test(value),
    "Conversation text must be nonblank literal text without expressions, braces or control/format characters other than LF.",
  );

export const conversationTurnsSchema = z
  .array(
    z
      .object({
        question: literalText,
        referenceAnswer: literalText,
      })
      .strict(),
  )
  .min(2)
  .max(6);

export const conversationTestCaseInputSchema = z
  .object({
    agentId: guid,
    componentId: guid,
    name: z
      .string()
      .min(1)
      .max(200)
      .refine(
        (value) => value.trim().length > 0 && !/[\p{Cc}\p{Cf}]/u.test(value),
        "Name must be nonblank and contain no control/format characters.",
      ),
    conversationTurns: conversationTurnsSchema,
    confirmCreate: z.boolean().optional(),
  })
  .strict();

/** O(total text length); closed plain-text subset of the public authoring schema. */
export function buildConversationTestCaseYaml(
  turns: z.infer<typeof conversationTurnsSchema>,
): string {
  const validated = conversationTurnsSchema.parse(turns);
  const lines = ["kind: MultiTurnEvaluationCase", "source: Manual", "activities:"];
  for (const turn of validated) {
    for (const [role, text] of [
      ["user", turn.question],
      ["agent", turn.referenceAnswer],
    ] as const) {
      lines.push(
        "  - activity:",
        "      kind: Message",
        "      text:",
        `        - ${JSON.stringify(text)}`,
        "      value:",
        "        from:",
        `          role: ${role}`,
      );
    }
  }
  return lines.join("\n") + "\n";
}

/** Advisory only: do not impose this builder's narrower paired-text subset on native cases. */
export function lintConversationTestCaseSource(data: string): string[] {
  if (data.length > 200_000)
    return ["conversation case source exceeds this reader's inspection bound"];
  try {
    const document = parseDocument(data, { uniqueKeys: true, strict: true });
    if (document.errors.length > 0 || !isMap(document.contents)) {
      return ["conversation case must be one valid YAML mapping; source validation is incomplete"];
    }
    const activities = document.contents.get("activities", true);
    if (!isSeq(activities) || activities.items.length === 0) {
      return [
        "conversation case requires a nonempty 'activities' sequence; reference responses are not runtime evidence",
      ];
    }
    return [];
  } catch {
    return ["conversation case YAML could not be inspected; source validation is incomplete"];
  }
}

const NOTICE =
  "EXPERIMENTAL: source/readback does not prove native serializer compatibility, grading, conversation execution or tool success. No evaluation was started, no membership changed and nothing was published.";
const safeErrors = new WeakMap<Error, string>();
export class CopilotConversationTestCaseError extends ApiError {}
export function getSafeCopilotConversationTestCaseErrorMessage(error: unknown): string | undefined {
  return error instanceof Error ? safeErrors.get(error) : undefined;
}
function authoredError(message: string): CopilotConversationTestCaseError {
  const error = new CopilotConversationTestCaseError(message, 502);
  safeErrors.set(error, message);
  return error;
}
const SELECT =
  "botcomponentid,name,componenttype,schemaname,_parentbotid_value,_parentbotcomponentid_value,data";
const record = (value: unknown): Record<string, unknown> | undefined =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
const sameGuid = (value: unknown, expected: string): boolean =>
  typeof value === "string" &&
  value.length === 36 &&
  GUID_PATTERN.test(value) &&
  value.toLowerCase() === expected;

export async function handleConversationTestCase(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = conversationTestCaseInputSchema.parse(input);
  const agentId = parsed.agentId.toLowerCase();
  const componentId = parsed.componentId.toLowerCase();
  if (agentId === componentId)
    throw new ValidationError("Use a distinct new componentId for the conversation case.");
  const content = buildConversationTestCaseYaml(parsed.conversationTurns);
  const hash = createHash("sha256").update(content, "utf8").digest("hex");
  if (parsed.confirmCreate !== true) {
    return [
      "Preview only: no authentication or Dataverse request was made.",
      `  agent ID: ${agentId}`,
      `  new component ID: ${componentId}`,
      `  name: ${JSON.stringify(parsed.name)}`,
      `  question/reference-answer pairs: ${parsed.conversationTurns.length}`,
      `  source sha256: ${hash}`,
      "  type: 19 / MultiTurnEvaluationCase; initially unparented",
      "Re-submit these exact arguments with confirmCreate=true to create this case once. The ID is a recovery target, not a retry permission.",
      "Reviewed source:",
      content,
      NOTICE,
    ].join("\n");
  }
  const api = context.api;
  if (!api)
    throw new ValidationError(
      "Dataverse must be configured before creating a conversation test case.",
    );
  let agent: Record<string, unknown> | undefined;
  let duplicate: Awaited<ReturnType<DataverseApi["queryRows"]>>;
  let schemaName: string;
  try {
    agent = record(await api.getRow("bots", agentId, "botid,schemaname"));
    if (
      !sameGuid(agent?.botid, agentId) ||
      typeof agent?.schemaname !== "string" ||
      !/^[A-Za-z_][A-Za-z0-9_]{0,99}(?![\s\S])/.test(agent.schemaname)
    ) {
      throw new Error("Unusable target");
    }
    schemaName = `${agent.schemaname}.testcase.Conversation${componentId.replaceAll("-", "")}`;
    duplicate = await api.queryRows("botcomponents", {
      select: "botcomponentid",
      filter: `botcomponentid eq ${componentId} or (_parentbotid_value eq ${agentId} and (schemaname eq '${escapeOData(schemaName)}' or name eq '${escapeOData(parsed.name)}'))`,
      top: 1,
    });
    if (!duplicate || !Array.isArray(duplicate.value) || duplicate.nextLink !== undefined)
      throw new Error("Incomplete lookup");
  } catch {
    throw authoredError(
      "Conversation-case preflight could not verify the exact agent and absence of an existing target. No create request was sent.",
    );
  }
  if (duplicate.value.length > 0) {
    throw new ValidationError(
      "A caller-visible component already matches the requested ID, name or schema. No create request was sent; inspect it instead of retrying with a new ID.",
    );
  }
  const payload = {
    botcomponentid: componentId,
    name: parsed.name,
    componenttype: 19,
    schemaname: schemaName,
    data: content,
    "parentbotid@odata.bind": `/bots(${agentId})`,
  };
  let created: Record<string, unknown> | undefined;
  try {
    created = record(await api.createRow("botcomponents", payload));
  } catch {
    throw authoredError(
      `Conversation-case create outcome is unknown for ${componentId}. Inspect that exact ID before deciding on another change; do not retry automatically.`,
    );
  }
  let verified = false;
  if (sameGuid(created?.botcomponentid, componentId)) {
    try {
      const row = record(await api.getRow("botcomponents", componentId, SELECT));
      verified =
        sameGuid(row?.botcomponentid, componentId) &&
        sameGuid(row?._parentbotid_value, agentId) &&
        row?.componenttype === 19 &&
        row.schemaname === schemaName &&
        row.name === parsed.name &&
        row.data === content &&
        row._parentbotcomponentid_value === null;
    } catch {
      // A successful create and an unavailable independent read are distinct outcomes.
    }
  }
  return [
    `Conversation-case create accepted for ${componentId}.`,
    `  schema name: ${schemaName}`,
    `  source sha256: ${hash}`,
    verified
      ? "Readback verified: exact source, name, schema, type, owner agent and empty parent matched at observation time."
      : "Readback not verified: inspect the exact component ID; do not retry automatically.",
    "This preflight is not an atomic uniqueness guarantee. The case is not runnable as a test set until separately linked to a homogeneous conversation EvaluationSet.",
    NOTICE,
  ].join("\n");
}
